import React, { useCallback, useEffect, useState } from 'react';
import { Box, Chip, Tooltip, CircularProgress } from '@mui/material';
import { Storage, Bolt } from '@mui/icons-material';
import { saudeService } from '../../services/api';
import { useUnidade } from '../../contexts/UnidadeContext';

export type FonteDados = 'snapshot' | 'tempo_real';

// Persistencia da fonte escolhida durante a sessao, no mesmo molde do QuadrimestreSelector:
// a escolha acompanha o operador ao navegar entre as telas /saude, e uma sessao nova volta
// ao default. Default = 'snapshot', porque o consolidado e o modo normal de uso — o tempo
// real existe para quando o gestor quer conferir o que entrou hoje.
const FONTE_STORAGE_KEY = 'saude:fonte';
const FONTE_PADRAO: FonteDados = 'snapshot';

/** Persiste a fonte escolhida no sessionStorage. */
export function saveFonte(fonte: FonteDados): void {
  try {
    sessionStorage.setItem(FONTE_STORAGE_KEY, fonte);
  } catch { /* ignora quota/privacidade */ }
}

/** Fonte em vigor nesta sessao. Usada tambem pelo interceptor do services/api.ts. */
export function getFonteInicial(): FonteDados {
  try {
    const raw = sessionStorage.getItem(FONTE_STORAGE_KEY);
    if (raw === 'tempo_real' || raw === 'snapshot') return raw;
  } catch { /* fallback abaixo */ }
  return FONTE_PADRAO;
}

/** "11/09 03:12" — data e hora da consolidacao, curtas o bastante para caber no chip. */
function formatarGeradoEm(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const data = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${data} ${hora}`;
}

interface Props {
  municipioId: number;
  fonte: FonteDados;
  onChange: (fonte: FonteDados) => void;
  /**
   * Quadrimestre exibido na tela, no formato "Q2-2026". Com vários quadrimestres guardados, a
   * etiqueta precisa mostrar a data da consolidação DAQUELE período — não a da mais recente,
   * que seria a do quadrimestre corrente mesmo estando olhando um passado.
   */
  quadrimestre?: string;
  /**
   * Painel consolidado que ESTA tela consome, quando não é um eixo: `cvat`, `cvat-saneamento`,
   * `qualidade-resumo` ou `socio-resumo`.
   *
   * Sem ele o chip fala pelos eixos (comportamento das telas de indicadores, inalterado). Com
   * ele, a data exibida é a daquele painel e o chip some se aquele painel específico ainda não
   * foi consolidado — senão a tela prometeria consolidado e serviria tempo real em silêncio.
   */
  painel?: string;
  /**
   * Painel de ESTADO ATUAL (Qualidade de Dados, Sociodemográficos): muda o texto da dica, porque
   * ali "consolidado em 14/09 03:12" se lê como competência, quando na verdade é a data da foto
   * do cadastro.
   */
  estadoAtual?: boolean;
}

/**
 * Alterna entre os indicadores consolidados de madrugada (D-1) e a consulta ao vivo.
 *
 * Nao aparece quando o municipio nao tem consolidacao disponivel — nesse caso tudo ja e tempo
 * real e um seletor so confundiria. Enquanto o status carrega tambem nao renderiza nada, para
 * nao piscar um controle que talvez nem exista naquele municipio.
 *
 * "Tempo real" so fica ATIVO com uma unidade de saude selecionada. Sem filtro de unidade o
 * consolidado cobre a tela inteira e responde em milissegundos, entao a consulta ao vivo so
 * troca o mesmo numero por dezenas de segundos de espera e por uma rajada no prontuario do
 * municipio. Com unidade a historia muda: varios recortes (CVAT, Sociodemograficos por equipe)
 * nao sao servidos do consolidado e caem no tempo real de qualquer forma, e ai a escolha passa
 * a valer alguma coisa.
 *
 * A unidade vem do `UnidadeContext`, que e global (montado no App) e nao de uma prop: as 7 telas
 * que usam este seletor compartilham a mesma selecao, e passar prop em todas so abriria espaco
 * para uma esquecer.
 */
const FonteDadosSelector: React.FC<Props> = ({ municipioId, fonte, onChange, quadrimestre, painel, estadoAtual }) => {
  const [status, setStatus] = useState<any>(null);
  const [carregando, setCarregando] = useState(true);
  const { unidadeSelecionada } = useUnidade();
  const temUnidade = !!unidadeSelecionada;

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    saudeService
      .getSnapshotStatus(municipioId)
      .then((s) => { if (ativo) setStatus(s); })
      .catch(() => { if (ativo) setStatus(null); })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [municipioId]);

  const alternar = useCallback(
    (nova: FonteDados) => {
      if (nova === fonte) return;
      saveFonte(nova);
      onChange(nova);
    },
    [fonte, onChange],
  );

  // Limpar a unidade enquanto se esta em "Tempo real" deixaria a tela consultando o prontuario do
  // municipio inteiro com o controle desativado — o usuario veria a lentidao de volta sem ter como
  // desfazer. Volta ao consolidado sozinho. So quando ha consolidado a que voltar: no municipio sem
  // consolidacao o seletor nem aparece e a fonte e irrelevante.
  useEffect(() => {
    if (status?.disponivel && !temUnidade && fonte === 'tempo_real') alternar('snapshot');
  }, [status?.disponivel, temUnidade, fonte, alternar]);

  if (carregando) return <CircularProgress size={16} sx={{ mx: 1 }} />;
  if (!status?.disponivel) return null;

  // Tela de painel (CVAT, Qualidade, Sociodemográficos): a data e a existência do consolidado são
  // as DAQUELE painel, não as dos eixos.
  if (painel) {
    const chave = estadoAtual ? 'atual' : quadrimestre;
    const doPainel = (status.paineis || []).find((p: any) => p.painel === painel && (!chave || p.chave === chave));
    if (!doPainel) return null;
    return renderChips(formatarGeradoEm(doPainel.gerado_em), fonte === 'snapshot', alternar, temUnidade, estadoAtual);
  }

  // Entrada do quadrimestre em tela; sem ela (ou sem quadrimestre informado), a mais recente.
  const doQuadrimestre = quadrimestre
    ? (status.quadrimestres || []).find((q: any) => q.quadrimestre === quadrimestre)
    : null;

  // Quadrimestre pedido que não foi consolidado: a tela está em tempo real de qualquer forma,
  // então o seletor não deve sugerir que há consolidado para ele.
  if (quadrimestre && !doQuadrimestre) return null;

  return renderChips(formatarGeradoEm(doQuadrimestre?.gerado_em ?? status.gerado_em), fonte === 'snapshot', alternar, temUnidade, false);
};

/** Os dois chips. Extraído para o caminho de eixo e o de painel renderizarem exatamente igual. */
function renderChips(
  geradoEm: string,
  consolidado: boolean,
  alternar: (f: FonteDados) => void,
  temUnidade: boolean,
  estadoAtual?: boolean,
) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip
        title={
          !geradoEm
            ? 'Dados consolidados na última madrugada'
            : estadoAtual
              // Em painel de estado atual a data não é competência: é quando a foto foi tirada.
              ? `O cadastro como estava em ${geradoEm}. Não consulta o prontuário, então a tela abre na hora — use "Tempo real" para ver as correções feitas hoje.`
              : `Indicadores consolidados em ${geradoEm}. Não consultam o prontuário, então a tela abre na hora.`
        }
      >
        <Chip
          size="small"
          icon={<Storage fontSize="small" />}
          label={geradoEm ? `Consolidado · ${geradoEm}` : 'Consolidado'}
          color={consolidado ? 'primary' : 'default'}
          variant={consolidado ? 'filled' : 'outlined'}
          onClick={() => alternar('snapshot')}
          sx={{ cursor: 'pointer' }}
        />
      </Tooltip>
      <Tooltip
        title={
          temUnidade
            ? 'Consulta o prontuário agora, incluindo o que foi registrado hoje. Pode demorar em municípios grandes.'
            : 'Disponível apenas com uma unidade de saúde selecionada. Sem filtro de unidade, os dados consolidados já cobrem a tela inteira e abrem na hora.'
        }
      >
        {/* O <span> não é decoração: um Chip desabilitado não emite eventos de mouse, e sem ele
            o Tooltip que explica o porquê nunca apareceria — justamente quando é mais necessário. */}
        <span style={{ display: 'inline-flex' }}>
          <Chip
            size="small"
            icon={<Bolt fontSize="small" />}
            label="Tempo real"
            color={!consolidado ? 'warning' : 'default'}
            variant={!consolidado ? 'filled' : 'outlined'}
            disabled={!temUnidade}
            onClick={temUnidade ? () => alternar('tempo_real') : undefined}
            sx={{ cursor: temUnidade ? 'pointer' : 'not-allowed' }}
          />
        </span>
      </Tooltip>
    </Box>
  );
}

export default FonteDadosSelector;
